import {
    Location as LocationGeneric,
    useLocation as useLocationGeneric,
    useMatch,
} from 'react-router';
import { FilterType } from '../Filter';

export type LocationState = {
    error?: string;
    returnLocation?: string;
    forceReload?: number;
};
export type Params = {
    filter: FilterType;
    category: string;
    id: number | null;
};

export type Location = LocationGeneric<LocationState>;

export function useLocation(): Location {
    return useLocationGeneric();
}

/**
 * Converts URL segment to FilterType value.
 */
export function filterTypeFromString(type: string): FilterType {
    if (type == 'newest') {
        return FilterType.NEWEST;
    } else if (type == 'unread') {
        return FilterType.UNREAD;
    } else if (type == 'starred') {
        return FilterType.STARRED;
    } else {
        throw new Error(`Invalid filter type: “${type}”`);
    }
}

/**
 * Converts FilterType value to string usable in URL.
 */
export function filterTypeToString(type: FilterType): string {
    if (type == FilterType.NEWEST) {
        return 'newest';
    } else if (type == FilterType.UNREAD) {
        return 'unread';
    } else if (type == FilterType.STARRED) {
        return 'starred';
    } else {
        throw new Error(`Invalid filter type: “${type}”`);
    }
}

function generatePath({
    filter,
    category,
    id,
}: {
    filter: string;
    category: string;
    id: string | null;
}): string {
    return `/${filter}/${category}${id ? `/${id}` : ''}`;
}

export function useEntriesParams(): Params | null {
    const match = useMatch(':filter/:category/:id?');

    if (match === null) {
        return null;
    }

    const { params } = match;

    if (!params.filter || !params.category) {
        // Note: it isn't possible to reach this part of the code
        //       This is to keep the TypeScript compiler happy
        return null;
    }

    const filterValid = /^(newest|unread|starred)$/.test(params.filter);
    const categoryValid = /^(all|tag-.+|source-[0-9]+)$/.test(params.category);
    const idValid = params.id === undefined || /^\d+$/.test(params.id);

    if (!filterValid || !idValid || !categoryValid) {
        return null;
    }

    return {
        filter: filterTypeFromString(params.filter),
        category: params.category,
        id: params.id === undefined ? null : parseInt(params.id, 10),
    };
}

type EntriesLinkParams = {
    filter?: FilterType;
    category?: string;
    id?: string;
    search?: string;
};

export function makeEntriesLinkLocation(
    location: Location,
    { filter, category, id, search }: EntriesLinkParams,
): { pathname: string; search: string } {
    const queryString = new URLSearchParams(location.search);

    let path: string;
    if (location.pathname.match(/^\/(newest|unread|starred)\//) !== null) {
        const [, ...segments] = location.pathname.split('/');

        path = generatePath({
            filter: filter ?? segments[0],
            category: category ?? segments[1],
            id: typeof id !== 'undefined' ? id : segments[2],
        });
    } else {
        path = generatePath({
            // TODO: change default from config
            filter: filter ?? 'unread',
            category: category ?? 'all',
            id,
        });
    }

    if (typeof search !== 'undefined') {
        if (search) {
            queryString.set('search', search);
        } else {
            queryString.delete('search');
        }
    }

    return {
        pathname: path,
        search: queryString.toString(),
    };
}

export function makeEntriesLink(
    location: Location,
    params: EntriesLinkParams,
): string {
    const { pathname, search } = makeEntriesLinkLocation(location, params);

    return pathname + (search !== '' ? `?${search}` : '');
}

export function forceReload(location: Location): LocationState {
    const state = location.state ?? {};

    return {
        ...state,
        forceReload: (state.forceReload ?? 0) + 1,
    };
}

export function useForceReload(): LocationState {
    const location = useLocation();
    return forceReload(location);
}
